// ────────────────────────────────────────────────────────────────────────────
// Inter-frame scroll-stability gate flag (b17u).
//
// The b17u gate (frameStabilityGate) skips per-cell classification on frames
// captured mid-scroll, which after b17t were the dominant source of bogus
// "never seen" cells (77% of cells in the post-b17t device log read
// numberPosition:"center" with sprite "none" — the mid-scroll signature).
//
// Mirroring the blur gate (frameQualityFlag), this flag lets us (a) retune the
// band-diff threshold per build without a code change and (b) fully bypass the
// stability gate as a kill-switch if it ever over-tightens and starves the
// pipeline of frames (the b13c lesson: too strict a gate dropped 100% of
// frames). Bypassing leaves the blur gate and all downstream gates intact.
//
// Resolution order (first wins):
//
//   1. Build-time env `VITE_DEX_SCROLL_GATE`:
//        "off" / "0"      → bypass the scroll-stability gate entirely.
//        a number > 0     → use as the max band-diff (luma units) threshold.
//
//   2. localStorage["dex.scan.scrollGate"]: same grammar as the env var.
//        Sticky device override via Safari Web Inspector.
//
//   3. URL hash `#dex-scroll-off` → bypass for this session (promoted to LS).
//
//   4. Otherwise: gate ON at the default threshold below.
// ────────────────────────────────────────────────────────────────────────────

// Conservative default: catch obvious scroll motion while letting microframe
// jitter through. The band profile is in luma units (0..255); a static dwell
// frame differs from the previous frame by only sensor/compression noise
// (typically < 2 luma units averaged across 64 buckets), while a frame mid-
// scroll shifts whole header glyphs through the band and differs by many units.
// 8.0 sits well above the noise floor and well below a real scroll shift, so it
// rejects scrolling frames without dropping held-still frames. Retune on device
// via localStorage["dex.scan.scrollGate"] once a real-frame diff histogram is
// available; bypass entirely with `#dex-scroll-off`.
export const DEX_SCROLL_STABILITY_DEFAULT_DIFF_MAX = 8.0;

export const DEX_SCROLL_STABILITY_LS_KEY = "dex.scan.scrollGate";
export const DEX_SCROLL_STABILITY_OFF_URL_HASH = "dex-scroll-off";

export interface ScrollStabilityConfig {
  /** When false, the inter-frame scroll-stability gate is bypassed entirely. */
  enabled: boolean;
  /** Max band diff (luma units) a frame may have and still classify per-cell. */
  diffMax: number;
}

let cached: ScrollStabilityConfig | null = null;

function parseRaw(raw: string | undefined | null): Partial<ScrollStabilityConfig> | null {
  if (raw == null) return null;
  const v = raw.trim().toLowerCase();
  if (v === "off" || v === "0" || v === "false") return { enabled: false };
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return { enabled: true, diffMax: n };
  return null;
}

function resolve(): ScrollStabilityConfig {
  const base: ScrollStabilityConfig = {
    enabled: true,
    diffMax: DEX_SCROLL_STABILITY_DEFAULT_DIFF_MAX,
  };

  // 1. Env.
  try {
    const raw = (import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }).env?.VITE_DEX_SCROLL_GATE;
    const parsed = parseRaw(raw);
    if (parsed) return { ...base, ...parsed };
  } catch {
    // ignore
  }

  if (typeof window === "undefined") return base;

  // 2. localStorage.
  try {
    const parsed = parseRaw(window.localStorage.getItem(DEX_SCROLL_STABILITY_LS_KEY));
    if (parsed) return { ...base, ...parsed };
  } catch {
    // ignore
  }

  // 3. URL hash off-probe; promote to LS so reloads stay off.
  const hash = window.location?.hash?.toLowerCase() ?? "";
  if (hash.includes(DEX_SCROLL_STABILITY_OFF_URL_HASH)) {
    try {
      window.localStorage.setItem(DEX_SCROLL_STABILITY_LS_KEY, "off");
    } catch {
      // ignore
    }
    return { ...base, enabled: false };
  }

  return base;
}

/**
 * Resolve the inter-frame scroll-stability gate configuration. Resolves at most
 * once per page-load and caches the result.
 */
export function getScrollStabilityConfig(): ScrollStabilityConfig {
  if (cached !== null) return cached;
  cached = resolve();
  return cached;
}

/** Test-only escape hatch. */
export function __resetScrollStabilityFlagCache(): void {
  cached = null;
}
